import React, { useRef, useState } from 'react';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import AlertExecution from '@app/model/alert/AlertExecution';
import { useQuery } from '@tanstack/react-query';
import AlertService from '@app/model/alert/service/AlertService';
import { AlertExecutionQueryKeys } from '@app/model/alert/service/AlertQueryKeys';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/16/solid';
import Utilities from '@util/Utilities';
import PriceComparisonAlertRulePricePoint from '@app/model/alert/PriceComparisonAlertRulePricePoint';
import _isEmpty from 'lodash/isEmpty';
import PriceComparisonAlertExecutionMostImportantNotificationsViewFragment
	from '@app/site/secured/rateshopper/rates/alert-execution/fragment/price-comparison/PriceComparisonAlertExecutionMostImportantNotificationsViewFragment';
import PriceComparisonAlertExecutionAllNotificationsViewFragment
	from '@app/site/secured/rateshopper/rates/alert-execution/fragment/price-comparison/PriceComparisonAlertExecutionAllNotificationsViewFragment';
import Message from '@app/component/message/Message';
import MarketStateDetailsModal from '@app/site/secured/rateshopper/rates/alert/modal/MarketStateDetailsModal';
import PriceComparisonAlertExecutionUpdatedNotificationsFragment
	from '@app/site/secured/rateshopper/rates/alert-execution/fragment/price-comparison/PriceComparisonAlertExecutionUpdatedNotificationsFragment';
import Refunding from '@app/model/market_state/Refunding';
import PageHeader from '@app/component/page/PageHeader';

const PriceComparisonAlertExecutionFragment: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	alertExecution: AlertExecution,
}> = ({ companyHotel, alertExecution }) => {
	const tabs = [
		{ id: 'MOST_IMPORTANT', name: 'Most Important Alerts' },
		{ id: 'ALL', name: 'All Alerts' },
	];
	const [currentTabId, setCurrentTabId] = useState<string>(tabs[0].id);

	const marketStateDetailsModal = useRef<any>(undefined);

	const { data: notificationOutputs, isLoading, isSuccess } = useQuery({
		queryFn: () => AlertService.findPriceComparisonAlertExecutionNotifications(companyHotel.id, alertExecution.id),
		queryKey: AlertExecutionQueryKeys.notifications(alertExecution.id),
	});

	if (isLoading) {
		return <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8">
			{[...Array(3)].map((x, i) =>
				<div key={i} className="mt-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
					<div className="w-full animate-pulse space-y-4">
						<div className="h-4 w-36 rounded-full bg-gray-200"></div>
						<div className="h-3 w-24 rounded-full bg-gray-200"></div>
						<div className="h-20 rounded-lg bg-gray-100"></div>
					</div>
				</div>,
			)}
		</div>;
	}

	if (isSuccess) {

		const updatedNotificationOutputs = notificationOutputs.filter(o => o.isNew || o.isUpdated);
		const newNotificationOutputs = notificationOutputs.filter(o => o.isNew);
		const areAllNew = updatedNotificationOutputs.length === notificationOutputs.length;
		const propertyName = companyHotel.mainHotel?.competitors?.[0]?.hotel?.name ?? companyHotel.mainHotel?.name ?? 'My Property';
		const pricePointLabel = alertExecution.alertOutput.priceComparisonAlertRule.pricePoint === PriceComparisonAlertRulePricePoint.MEDIAN ? 'Median'
			: alertExecution.alertOutput.priceComparisonAlertRule.pricePoint === PriceComparisonAlertRulePricePoint.MIN ? 'Least Expensive'
				: 'Most Expensive';

		return <>
			<main className="bg-gray-50">

				<div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8">
					<div className="text-right mb-1 mr-1">
					<span
						className="text-xs text-gray-400">Executed at {Utilities.formatDateString(alertExecution.executionDate, 'DD/MM/YYYY HH:mm')}</span>
					</div>
					{!!alertExecution.alertOutput.alert &&
						<section
							className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
							<div className="flex flex-col gap-1">
								<h1
									className="text-lg font-semibold leading-6 text-gray-950">{alertExecution.alertOutput.alert.name}</h1>
								<p className="text-xs font-medium text-gray-500">{propertyName}</p>
							</div>

							<div className="mt-7">
								<p className="text-sm font-semibold text-gray-900">Competitors</p>
								<div className="mt-3 flex flex-wrap gap-2">
									{alertExecution.alertOutput.competitors!.map(c => <span
										key={c.id}
										className="inline-flex items-center rounded bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200">{c.name}</span>)}
								</div>
							</div>

							<div className="mt-7">
								<p className="text-sm font-semibold text-gray-900">Configuration</p>
								<dl className="mt-4 grid grid-cols-1 gap-x-16 gap-y-5 text-sm sm:grid-cols-2">
									<div>
										<dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Type</dt>
										<dd className="mt-1 text-gray-800">Rate Comparison</dd>
									</div>
									<div>
										<dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Price Calculation</dt>
										<dd className="mt-1 text-gray-800">{pricePointLabel}</dd>
									</div>
									<div>
										<dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Range</dt>
										<dd
											className="mt-1 text-gray-800">{alertExecution.alertOutput.priceComparisonAlertRule.priceRange!.from}%
											to {alertExecution.alertOutput.priceComparisonAlertRule.priceRange!.to}%
										</dd>
									</div>
									<div>
										<dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Rate</dt>
										<dd
											className="mt-1 text-gray-800">{alertExecution.alertOutput.alert.filter.refunding === Refunding.FULL ? 'Lowest Flex' : 'Lowest'}</dd>
									</div>
								</dl>
							</div>

							<div
								className="mt-8 flex flex-col gap-4 border-t border-gray-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
								<div className="flex items-end gap-6">
									<div className="flex items-baseline gap-1.5">
										<span
											className="text-2xl font-semibold leading-none text-gray-950">{alertExecution.numberOfNotifications}</span>
										<span className="text-sm text-gray-500">Total Alerts</span>
									</div>
									<div className="flex items-baseline gap-1.5">
										<span
											className="text-2xl font-semibold leading-none text-gray-950">{newNotificationOutputs.length}</span>
										<span className="text-sm text-gray-500">New Alerts</span>
									</div>
								</div>
								<Link
									to={`/secured/rateshopper/rates/alert/${alertExecution.alertOutput.alert.type}/${alertExecution.alertOutput.alert.id}`}
									className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-sm hover:bg-blue-700">Edit
									Alert</Link>
							</div>
						</section>}
					{!areAllNew && !_isEmpty(updatedNotificationOutputs) &&
						<PriceComparisonAlertExecutionUpdatedNotificationsFragment alertExecution={alertExecution}
						                                                           notificationOutputs={updatedNotificationOutputs}
						                                                           marketStateDetailsModal={marketStateDetailsModal} />}
					{!_isEmpty(notificationOutputs) && <div className="mt-8">
						<div className="sm:hidden">
							<label htmlFor="tabs" className="sr-only">
								Select a tab
							</label>
							<select
								id="tabs"
								name="tabs"
								value={currentTabId}
								onChange={(e) => setCurrentTabId(e.target.value)}
								className="block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
							>
								{tabs.map((tab) => (
									<option key={tab.id} value={tab.id}>{tab.name}</option>
								))}
							</select>
						</div>
						<div className="hidden sm:block">
							<div className="border-b border-gray-200">
								<nav aria-label="Tabs" className="-mb-px flex gap-6">
									{tabs.map((tab) => (
										<a
											key={tab.name}
											onClick={() => setCurrentTabId(tab.id)}
											aria-current={currentTabId === tab.id ? 'page' : undefined}
											className={Utilities.classNames(
												currentTabId === tab.id
													? 'border-blue-600 text-blue-600'
													: 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
												'cursor-pointer whitespace-nowrap border-b-2 px-6 py-4 text-sm font-semibold',
											)}
										>
											{tab.name}
										</a>
									))}
								</nav>
							</div>
						</div>
					</div>}
					{!_isEmpty(notificationOutputs) && currentTabId === 'MOST_IMPORTANT' &&
						<PriceComparisonAlertExecutionMostImportantNotificationsViewFragment
							alertExecution={alertExecution}
							notificationOutputs={notificationOutputs}
							marketStateDetailsModal={marketStateDetailsModal} />}
					{!_isEmpty(notificationOutputs) && currentTabId === 'ALL' &&
						<PriceComparisonAlertExecutionAllNotificationsViewFragment alertExecution={alertExecution}
						                                                           notificationOutputs={notificationOutputs}
						                                                           marketStateDetailsModal={marketStateDetailsModal} />}
					{(!alertExecution || _isEmpty(notificationOutputs)) &&
						<div className="mx-auto max-w-5xl py-6">
							<Message state="warning">No results found</Message>
						</div>}
				</div>
			</main>
			<MarketStateDetailsModal companyHotel={companyHotel} alertFilter={alertExecution.alertOutput.alert.filter}
			                         ref={marketStateDetailsModal} />
		</>;
	}
	return <></>;
};

export default PriceComparisonAlertExecutionFragment;
